import { useEffect, useState } from 'react';
import { Route, Switch } from 'react-router-dom';
import { GridRowParams } from '@material-ui/data-grid';
import { observer } from 'mobx-react-lite';
import { toJS } from 'mobx';
import { ReactComponent as PublicCompanyIcon } from '../../images/Locations/publicCompany.svg';
import { PartnerStatusBadge } from '../../components/other/CellBadges';
import { partnerDetailsParameters, partnerDetailsRoute, route } from '../../utils/routing';
import { gridColumn } from '../../utils/gridUtils';
import TextLink from '../../components/other/TextLink';
import PartnerDetails from './PartnerDetails';
import { drawerStore } from '../../state/drawerStore';
import { FormInputTypes, COUNTRIES } from '../../utils/constants';
import { Api } from '../../api';
import { PAGES } from '../../components/main/MainComponent';
import { usePaginatedProvider } from '../../state/paginatedProviderStore';
import { VMPaginatedGrid } from '../../components/main/VMPaginatedGrid';

const partnerCreateInputs = [
  { name: 'name', type: FormInputTypes.textField },
  { name: 'address', type: FormInputTypes.textField },
  { name: 'address2', label: 'Address 2', type: FormInputTypes.textField },
  { name: 'zip', type: FormInputTypes.textField },
  { name: 'city', type: FormInputTypes.textField },
  { name: 'country', type: FormInputTypes.select, options: COUNTRIES },
  { name: 'vat', label: 'VAT', type: FormInputTypes.textField },
];

const PartnersListPage = observer(() => {
  const partnersProvider = usePaginatedProvider(undefined, undefined, false);
  const [sortableFields, setSortableFields] = useState([]);
  const [filterParams, setFilterParams] = useState({});

  const loadPartners = (filters = filterParams) => {
    partnersProvider.setRequest((offset, sortBy, sortOrder) =>
      Api.charge.listPartners({ ...filters, skip: offset, take: 20, sortBy, sortOrder }),
    );

    partnersProvider.setOnFulfilled((_data, _count, sortable) => {
      if (sortable) {
        setSortableFields(sortable);
      }
    });
    partnersProvider.reloadResource();
  };

  useEffect(() => {
    loadPartners();
  }, []);

  const onClickConfirmAdd = () => {
    const data = toJS(drawerStore.values);
    Api.charge.createPartner(data).then(partnersProvider.reloadResource);
  };

  const onClickPartnerAdd = () => {
    drawerStore.openDrawer({
      label: 'Add Partner',
      inputs: partnerCreateInputs,
      clickCallback: onClickConfirmAdd,
    });
  };

  const partnerColumns = [
    gridColumn('', {
      flex: 0.5,
      sortableFields,
      minWidth: 60,
      renderCell: () => (
        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 'normal' }}>
          <PublicCompanyIcon style={{ marginBottom: '4px' }} />
        </div>
      ),
    }),
    gridColumn('partnerId', {
      headerName: 'Partner ID',
      flex: 2,
      sortableFields,
      minWidth: 120,
      renderCell: (params: GridRowParams) => (
        <TextLink
          label={params.row.partnerId || 'n/a'}
          to={partnerDetailsParameters(params.row.id)}
        />
      ),
    }),
    gridColumn('name', {
      flex: 3,
      sortableFields,
      minWidth: 200,
      renderCell: (params: GridRowParams) => (
        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 'normal' }}>
          <TextLink label={params.row.name || 'n/a'} to={partnerDetailsParameters(params.row.id)} />
          <div
            style={{
              display: 'flex',
              gap: '0.5rem',
              fontSize: '0.9rem',
              color: 'gray',
              alignItems: 'center',
            }}
          >
            <span>{params.row.city}</span>
            {params.row.country && <span>{params.row.country}</span>}
          </div>
        </div>
      ),
    }),
    gridColumn('roles', {
      headerName: 'Roles',
      flex: 3,
      sortableFields,
      minWidth: 200,
      renderCell: (params: GridRowParams) => (
        <div style={{ display: 'flex', flexWrap: 'wrap' }}>
          {(params.row.roles || []).map((role: string) => (
            <span
              key={role}
              style={{
                backgroundColor: '#00E8A8',
                borderRadius: '24px',
                padding: '0 0.5rem',
                marginRight: '0.4rem',
                fontSize: '0.75rem',
                lineHeight: '1.4',
              }}
            >
              {role}
            </span>
          ))}
        </div>
      ),
    }),
    gridColumn('status', {
      flex: 1.5,
      sortableFields,
      minWidth: 120,
      renderCell: (params: GridRowParams) => <PartnerStatusBadge status={params.row.status} />,
    }),
  ];

  const filterCallback = (param?: string, val?: string) => {
    const newFilters: any = {};
    if (param && val) {
      newFilters[param] = val;
    }
    setFilterParams(newFilters);
    loadPartners(newFilters);
  };

  return (
    <div style={{ width: '100%' }}>
      <VMPaginatedGrid
        onClickAdd={onClickPartnerAdd}
        label="Partners"
        count={partnersProvider.count}
        rows={partnersProvider.data}
        columns={partnerColumns}
        onPageOffsetChange={partnersProvider.onPageChange}
        onSortTypeChange={partnersProvider.setSort}
        filterParamOptions={[
          { label: 'Name', value: 'name' },
          { label: 'Partner ID', value: 'partnerId' },
          { label: 'Status', value: 'status' },
        ]}
        filterCallback={filterCallback}
        rowHeight={60}
      />
    </div>
  );
});

const PartnersPage = () => (
  <Switch>
    <Route exact path={route(PAGES.Partners)} component={PartnersListPage} />
    <Route path={partnerDetailsRoute()} component={PartnerDetails} />
  </Switch>
);

export default PartnersPage;
